import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { apiClient, formatSAR } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import {
  Plus, Megaphone, Eye, MousePointerClick, TrendingUp, Edit3, Trash2,
  CreditCard, Loader2, Clock, CheckCircle2, AlertCircle, Pause, BarChart3,
} from "lucide-react";

const STATUS_BADGE = {
  active: { ar: "نشطة", en: "Active", bg: "#16a34a20", border: "#16a34a", text: "#15803d", icon: CheckCircle2 },
  draft: { ar: "مسودة", en: "Draft", bg: "#3a3a3a10", border: "#3a3a3a30", text: "#6b6b6b", icon: Edit3 },
  pending_payment: { ar: "بانتظار الدفع", en: "Awaiting payment", bg: "#f5a72420", border: "#f5a724", text: "#a16207", icon: CreditCard },
  scheduled: { ar: "مجدولة", en: "Scheduled", bg: "#c9a55c20", border: "#c9a55c", text: "#8a6f2e", icon: Clock },
  paused: { ar: "متوقفة", en: "Paused", bg: "#6b6b6b20", border: "#6b6b6b", text: "#3a3a3a", icon: Pause },
  expired: { ar: "منتهية", en: "Expired", bg: "#6b6b6b10", border: "#6b6b6b50", text: "#6b6b6b", icon: AlertCircle },
};

export default function AdvertiserDashboard() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [state, setState] = useState({ data: null, loading: true });

  const load = () => {
    apiClient.get("/advertiser/campaigns")
      .then(({ data }) => setState({ data, loading: false }))
      .catch(() => setState(s => ({ ...s, loading: false })));
  };

  useEffect(() => { if (user) load(); }, [user]);

  if (user === undefined) return <div className="p-10 text-[#525252]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/advertiser/dashboard" }} replace />;

  const { data, loading } = state;
  if (loading || !data) return <div className="p-10 text-[#525252]"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const checkout = async (cid) => {
    try {
      const { data: res } = await apiClient.post(`/advertiser/campaigns/${cid}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.assign(res.url);
    } catch (e) {
      alert(e.response?.data?.detail || "Error");
    }
  };

  const del = async (cid) => {
    if (!window.confirm(lang === "ar" ? "حذف هذه الحملة؟" : "Delete this campaign?")) return;
    try {
      await apiClient.delete(`/advertiser/campaigns/${cid}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Error");
    }
  };

  const s = data.stats || {};
  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-10" data-testid="advertiser-dashboard">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-8">
        <div>
          <div className="overline overline-gold mb-2">{lang === "ar" ? "بوابة المُعلنين" : "Advertiser portal"}</div>
          <h1 className="font-display text-4xl tracking-[-0.02em] font-medium">{lang === "ar" ? "حملاتي الإعلانية" : "My ad campaigns"}</h1>
        </div>
        <button onClick={() => navigate("/advertiser/campaigns/new")} className="btn-primary" data-testid="new-campaign-btn">
          <Plus className="w-4 h-4" /> {lang === "ar" ? "حملة جديدة" : "New campaign"}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
        <Stat icon={Megaphone} label={lang === "ar" ? "إجمالي الحملات" : "Total"} value={s.total_campaigns || 0} />
        <Stat icon={CheckCircle2} label={lang === "ar" ? "نشطة" : "Active"} value={s.active || 0} accent />
        <Stat icon={Eye} label={lang === "ar" ? "ظهور" : "Impressions"} value={(s.impressions || 0).toLocaleString()} />
        <Stat icon={MousePointerClick} label={lang === "ar" ? "نقرات" : "Clicks"} value={(s.clicks || 0).toLocaleString()} />
        <Stat icon={TrendingUp} label="CTR" value={`${s.ctr_pct || 0}%`} />
      </div>

      <div className="surface rounded-xl p-4 mb-6 flex items-center gap-3 text-sm">
        <BarChart3 className="w-4 h-4 text-[#8a6f2e]" />
        <span className="text-[#3a3a3a]">{lang === "ar" ? `إجمالي ما أنفقت: ${formatSAR(s.total_spent_sar || 0)} ريال` : `Total spent: ${formatSAR(s.total_spent_sar || 0)} SAR`}</span>
      </div>

      {/* Campaigns list */}
      {data.items.length === 0 ? (
        <div className="surface rounded-xl p-16 text-center">
          <Megaphone className="w-12 h-12 mx-auto text-[#c9a55c] opacity-60 mb-4" />
          <h3 className="font-display text-2xl mb-2">{lang === "ar" ? "لا توجد حملات بعد" : "No campaigns yet"}</h3>
          <p className="text-[#6b6b6b] mb-6">{lang === "ar" ? "أنشئ أول حملة إعلانية وابدأ بالوصول لجمهور كارتال" : "Launch your first campaign and reach the CARTAL audience"}</p>
          <button onClick={() => navigate("/advertiser/campaigns/new")} className="btn-primary mx-auto" data-testid="empty-new-campaign">
            <Plus className="w-4 h-4" /> {lang === "ar" ? "إنشاء حملة" : "Create campaign"}
          </button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {data.items.map(c => {
            const eff = c.effective_status || c.status;
            const st = STATUS_BADGE[eff] || STATUS_BADGE.draft;
            const Icon = st.icon;
            const ctr = c.impressions ? ((c.clicks / c.impressions) * 100).toFixed(2) : 0;
            return (
              <div key={c.id} className="surface rounded-xl overflow-hidden" data-testid={`my-campaign-${c.id}`}>
                {c.image && <img src={c.image} alt="" className="w-full h-32 object-cover" />}
                <div className="p-5">
                  <div className="flex justify-between items-start gap-2 mb-3">
                    <div>
                      <h3 className="font-display text-lg">{c.title}</h3>
                      <div className="text-xs text-[#6b6b6b]">{c.advertiser_name}</div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border" style={{ background: st.bg, borderColor: st.border, color: st.text }}>
                      <Icon className="w-3 h-3" /> {lang === "ar" ? st.ar : st.en}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center text-xs my-3 py-3 border-y border-[#0a0a0a]/[0.06]">
                    <div><div className="text-[#8a8a8a]">{lang === "ar" ? "ظهور" : "Views"}</div><div className="font-medium">{(c.impressions || 0).toLocaleString()}</div></div>
                    <div><div className="text-[#8a8a8a]">{lang === "ar" ? "نقرات" : "Clicks"}</div><div className="font-medium">{(c.clicks || 0).toLocaleString()}</div></div>
                    <div><div className="text-[#8a8a8a]">CTR</div><div className="font-medium">{ctr}%</div></div>
                  </div>

                  <div className="text-xs text-[#3a3a3a] space-y-1">
                    <div className="flex justify-between"><span className="text-[#8a8a8a]">{lang === "ar" ? "الموقع:" : "Placement:"}</span> <span>{c.placement}</span></div>
                    <div className="flex justify-between"><span className="text-[#8a8a8a]">{lang === "ar" ? "المدة:" : "Period:"}</span> <span>{c.start_date} → {c.end_date} ({c.days} {lang === "ar" ? "يوم" : "days"})</span></div>
                    <div className="flex justify-between font-medium text-[#0a0a0a]"><span className="text-[#8a8a8a]">{lang === "ar" ? "السعر الكلي:" : "Total:"}</span> <span>{formatSAR(c.price_sar || 0)} SAR</span></div>
                  </div>

                  <div className="flex gap-2 mt-4 flex-wrap">
                    {eff === "draft" && (
                      <>
                        <button onClick={() => checkout(c.id)} className="btn-primary text-xs" data-testid={`pay-${c.id}`}>
                          <CreditCard className="w-3 h-3" /> {lang === "ar" ? `ادفع ${formatSAR(c.price_sar)} ريال` : `Pay ${formatSAR(c.price_sar)} SAR`}
                        </button>
                        <Link to={`/advertiser/campaigns/${c.id}/edit`} className="btn-ghost text-xs"><Edit3 className="w-3 h-3" /> {lang === "ar" ? "تعديل" : "Edit"}</Link>
                        <button onClick={() => del(c.id)} className="btn-ghost text-xs text-red-700 border-red-300"><Trash2 className="w-3 h-3" /></button>
                      </>
                    )}
                    {eff === "pending_payment" && (
                      <button onClick={() => checkout(c.id)} className="btn-primary text-xs" data-testid={`pay-${c.id}`}>
                        <CreditCard className="w-3 h-3" /> {lang === "ar" ? "أكمل الدفع" : "Complete payment"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`surface rounded-xl p-4 ${accent ? "border-[#c9a55c]/40" : ""}`}>
      <div className="flex items-center gap-2 text-xs text-[#6b6b6b] uppercase tracking-widest mb-2"><Icon className="w-3 h-3" /> {label}</div>
      <div className={`font-display text-2xl ${accent ? "text-[#8a6f2e]" : "text-[#0a0a0a]"}`}>{value}</div>
    </div>
  );
}
